import { ApolloQueryResult } from '@apollo/client';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import get from 'lodash/get';
import set from 'lodash/set';
import isNil from 'lodash/isNil';
import React, { useState, useEffect } from 'react';
import { setPricingEligibility } from '@atlassian/cssxp-types-billing';
import {
  useSidebar,
  useFormSubmit,
  useNotification,
  ErrorComponents,
  InfoPanel,
  AddressFormSection,
  successNotificationBuilder,
  NotificationTypes,
} from '@css-xp/base';
import SectionMessage from '@atlaskit/section-message';
import { updatePricingEligibility } from '@atlassian/temp-proxies';
import ChangePricingEligibility from '../component/update-pricing-eligibility';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import { AnalyticsTrackAction, AnalyticsTrackSubject, analytics } from '@atlassian/temp-analytics';

/**
 * This component is required to pass closeSidebar to the form. calling useSidebar
 * directly on the calling page causes continuous re-rendering
 * */

interface FormData {
  pricingEligibility?: string;
}

export interface Props {
  transactionAccountId: string;
  pricingEligibility: string | null;
  billToAddress: CommerceAddress;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
}
const SetPricingEligibilitySidebar = (props: Props) => {
  const { closeSidebar } = useSidebar();
  const [isBillToAddressNotSet, setIsBillToAddressNotSet] = useState(false);

  useEffect(() => {
    setIsBillToAddressNotSet(isNil(props?.billToAddress?.country));
  }, [props.billToAddress]);

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<setPricingEligibility, any>({
    submitFormAction: updatePricingEligibility,
  });
  const submitEditBillToPartyDetails = (billingData: FormData, formApi: FormApi<FormData>) => {
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(billingData, key);
      set(transformedData, key, value);
    });

    const updateBillToPartyPayload: setPricingEligibility = {
      pricingEligibility: {
        [get(transformedData, 'pricingPlan')]: 'true',
      },
      transactionAccountId: props.transactionAccountId,
    };
    submitForm(updateBillToPartyPayload);
  };

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('update-pricing-eligibility-success')
      .withHeader('Successfully set Pricing Eligibility.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    analytics.sendTrackEvent({
      action: AnalyticsTrackAction.UPDATED,
      actionSubject: AnalyticsTrackSubject.PRICING_ELIGIBILITY,
    });
    setNotificationSent(true);
    closeSidebar();
    if (props.refetch) {
      props.refetch();
    }
  }

  return (
    <div style={{ maxWidth: '600px', padding: '25px' }}>
      <Form onSubmit={submitEditBillToPartyDetails}>
        {({ formProps, setFieldValue, submitting }) => (
          <form {...formProps}>
            <FormHeader title="Set pricing eligibility"></FormHeader>
            <FormSection>
              {formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="Unable to set pricing eligibility due to the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {isBillToAddressNotSet && (
                <SectionMessage appearance="warning" title="Add billing address" testId="billing-address-warning">
                  <div>
                    <p>Add billing address first to set pricing eligibility on it. </p>
                  </div>
                </SectionMessage>
              )}
            </FormSection>
            <ChangePricingEligibility {...props} />
            <FormSection>
              <h3>Bill to address</h3>
              <AddressFormSection
                {...props.billToAddress}
                isEditable={false}
                addressName="Bill to"
                setFieldValue={setFieldValue}
                isRequired={false}
              />
            </FormSection>
            <FormFooter align="start">
              <ButtonGroup>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={submitting || isProcessing}
                  isDisabled={isBillToAddressNotSet}
                  testId={submitting || isProcessing ? 'loading-button' : 'save-button'}
                >
                  Save
                </LoadingButton>
                <Button appearance="subtle" onClick={closeSidebar}>
                  Close
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
export default SetPricingEligibilitySidebar;
